<template>
  <div class="flex gap-2">
    <CIcon
      v-if="!isDragDropDisabled"
      class="font-900 color-primary3 mt-6 cursor-pointer flex-shrink-0"
      data-handle-group
      icon="grip-dots-vertical"
      size="20"
    />

    <div class="border-style-solid border-primary4 border-width-1 rounded-2 pa-4 flex-auto overflow-auto">
      <CCollapse v-model:expandedNames="expandedGroups" arrow-placement="left">
        <CCollapseItem :name="group.id">
          <template #header>
            <CTableInput
              :always-display-edit-icon="true"
              :class="{ 'w-full max-w-[400px]': getInputState(activeInputId === group.id) === 'edit' }"
              :max-length="64"
              :model-value="group.name"
              :state="getInputState(activeInputId === group.id)"
              value-class="color-text font-size-[18px] font-500"
              @cancel="clearActiveInput()"
              @edit="setActiveInput(group.id)"
              @update:modelValue="editGroupName(group.id, $event)"
            />
          </template>

          <template #header-extra>
            <div class="flex gap-2 items-center">
              <CButton
                v-tooltip="t('canvasUI.pptConversionSelector.deleteGroup')"
                circle
                :disabled="collection.groups.length === 1"
                secondary
                strong
                type="error"
                @click.stop="deleteGroup(group.id, group.name)"
              >
                <template #icon>
                  <CIcon icon="trash" size="16" />
                </template>
              </CButton>
            </div>
          </template>

          <VueDraggable
            class="flex flex-col"
            :disabled="isDragDropDisabled"
            :group="{
              name: 'slides',
            }"
            handle="[data-handle-slide]"
            :model-value="group.slides"
            v-bind="DragConfig"
            @update:model-value="reorderSlides(group.id, $event)"
          >
            <div v-for="(slide, index) in group.slides" :key="slide.id" class="flex">
              <div v-if="index !== 0" class="border-b-solid border-primary6"></div>

              <div class="flex gap-2 ma-2 flex-auto overflow-auto">
                <CIcon
                  v-if="!isDragDropDisabled"
                  class="color-primary4 flex-shrink-0 cursor-pointer font-900 self-center"
                  data-handle-slide
                  icon="grip-dots-vertical"
                  size="20"
                />

                <CollectionItem
                  :disabled-move-to-group="collection.groups.length === 1"
                  :is-first="index === 0"
                  :is-last="index === group.slides.length - 1"
                  :item="slide"
                  @moveDown="moveInDirection(group.id, slide, 1)"
                  @moveToGroup="openMoveSlideModal({ groupId: group.id, slide })"
                  @moveUp="moveInDirection(group.id, slide, -1)"
                />
              </div>
            </div>
          </VueDraggable>

          <div
            v-if="!group?.slides || group.slides.length === 0"
            class="bg-primary6 flex pa-4 justify-center items-center rounded-2 color-text2"
          >
            {{ t('canvasUI.pptConversionSelector.empty') }}
          </div>
        </CCollapseItem>
      </CCollapse>
    </div>
  </div>
</template>

<script setup lang="ts">
import CCollapseItem from '@lib/components/CCollapse/CCollapseItem.vue'
import CTableInput from '@lib/components/CTableInput/CTableInput.vue'
import CButton from '@lib/components/CButton/CButton.vue'
import CCollapse from '@lib/components/CCollapse/CCollapse.vue'
import { VueDraggable } from 'vue-draggable-plus'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import { nextTick, ref } from 'vue'
import { useTableInput } from '@ppt-conversion-selector/composables/table-input.use'
import { useAppStore } from '@ppt-conversion-selector/stores/app.store'
import { useI18n } from 'vue-i18n'
import type { CollectionPage } from '@collection-player/types/collection-player.type'
import type { CollectionGroupWithPages } from '@ppt-conversion-selector/types/model.type'
import CollectionItem from '@ppt-conversion-selector/components/CollectionItem.vue'
import { useMoveSlideModal } from '@ppt-conversion-selector/composables/move-slide-modal.use'
import { DragConfig } from '@ppt-conversion-selector/constants'

defineProps<{
  group: CollectionGroupWithPages
}>()

const { t } = useI18n()
const { open: openMoveSlideModal } = useMoveSlideModal()
const { getInputState, setActiveInput, clearActiveInput, activeInputId } = useTableInput()
const { collection, expandedGroups, deleteGroup, updateGroupName, updateSlides, updateGroups, isDragDropDisabled } =
  useAppStore()

const slideOrderChangeQueue = ref<{ [groupId: string]: CollectionPage[] }>({})

const moveInDirection = (groupId: string, slide: CollectionPage, direction: -1 | 1) => {
  const group = collection.value.groups.find((g) => g.id === groupId)
  const slides = group?.slides

  if (!slides) return

  const orderedArray = moveItemInArray(
    [...slides],
    group?.slides.indexOf(slide),
    group?.slides.indexOf(slide) + direction,
  )

  updateSlides(groupId, orderedArray)
}

const editGroupName = (groupId: string, name: string) => {
  updateGroupName(groupId, name)
  clearActiveInput()
}

const reorderSlides = (groupId: string, slides: CollectionPage[]) => {
  slideOrderChangeQueue.value[groupId] = slides

  updateGroups(
    collection.value.groups.map((group) =>
      group.id in slideOrderChangeQueue.value ? { ...group, slides: slideOrderChangeQueue.value[group.id] } : group,
    ),
  )

  nextTick(() => (slideOrderChangeQueue.value = {}))
}

function moveItemInArray(array: CollectionPage[], oldIndex: number, newIndex: number) {
  const [item] = array.splice(oldIndex, 1)
  array.splice(Math.min(newIndex, array.length), 0, item)

  return array
}
</script>

<style lang="scss" scoped>
:deep(.n-collapse-item__header-main) {
  font-size: 15px;
  line-height: 18px !important;
  letter-spacing: 0.15px;
  padding: 8px !important;

  .n-collapse-item-arrow {
    margin-left: 0 !important;
    font-size: 12px !important;
    margin-right: 8px !important;
  }
}
</style>
